import { useState, useEffect } from "react";
import { getAdminStats } from "@/lib/api";
import { naira } from "@/components/PricingSection";
import { BankCard } from "@/components/admin/BankCard";
import { UsageChart } from "@/components/admin/UsageChart";
import { Loader2 } from "lucide-react";
import { logWarn } from "@/lib/log";

function fmtDuration(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m ${seconds % 60}s`;
}

export const AdminStatsGrid = () => {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        getAdminStats()
            .then(setStats)
            .catch((e) => logWarn("admin stats load failed", e));
    }, []);

    if (!stats) {
        return (
            <div className="p-10 flex justify-center">
                <Loader2 className="w-5 h-5 text-blaze animate-spin" />
            </div>
        );
    }

    const cards = [
        { label: "Total Users", value: stats.total_users.toLocaleString(), testid: "stat-total-users" },
        { label: "New (7 Days)", value: stats.new_users_7d.toLocaleString(), testid: "stat-new-users" },
        { label: "Blocked", value: stats.blocked_users.toLocaleString(), testid: "stat-blocked-users", accent: stats.blocked_users > 0 },
        { label: "Revenue", value: naira(stats.revenue_ngn), testid: "stat-revenue", accent: true },
        { label: "Top-Ups", value: stats.total_topups.toLocaleString(), testid: "stat-topups" },
        { label: "Credits Outstanding", value: stats.credits_outstanding.toLocaleString(), testid: "stat-credits-outstanding" },
        { label: "Credits Consumed", value: stats.credits_used.toLocaleString(), testid: "stat-credits-used" },
        { label: "Stream Time", value: fmtDuration(stats.total_stream_seconds), testid: "stat-stream-time" },
        { label: "Total Streams", value: stats.total_streams.toLocaleString(), testid: "stat-total-streams" },
        { label: "Live Now", value: stats.active_streams.toLocaleString(), testid: "stat-active-streams", accent: stats.active_streams > 0 },
        { label: "Open Tickets", value: stats.open_tickets.toLocaleString(), testid: "stat-open-tickets", accent: stats.open_tickets > 0 },
    ];

    return (
        <div>
            <BankCard initialBalance={stats.bank_balance ?? 0} />
            <UsageChart bankBalance={stats.bank_balance ?? 0} />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
                {cards.map((c) => (
                    <div key={c.label} className="border border-white/10 bg-surface p-5" data-testid={c.testid}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                            {c.label}
                        </div>
                        <div className={`font-display font-bold text-2xl ${c.accent ? "text-blaze" : "text-white"}`}>
                            {c.value}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
